import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'

import NavBar from '../components/layout/NavBar'
import PagesFilterDrawer from '../components/pages/PagesFilterDrawer'
import BusinessPageCard from '../components/businesses/BusinessPageCard'
import DiscoverBookingModal from '../components/booking/DiscoverBookingModal'
import CreateBusinessModal from '../components/businesses/CreateBusinessModal'
import { boostedBusinesses, discoverBusinesses, myBusinesses, sponsoredCallAds } from '../api/businessesReader'
import type { BusinessProfile, SponsoredCallAdBusiness } from '../types/businesses'
import { getToken } from '../lib/auth'
import { DEFAULT_PAGES_FILTERS, normalizeCategory, parsePagesFilters, writePagesFilters, type PagesFilters } from '../lib/pagesFilters'
import { clearCreateBusinessIntent, setCreateBusinessIntent } from '../lib/pagesOnboarding'

export default function Pages() {
  const authed = Boolean(getToken())
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const qParam = searchParams.get('q') || ''
  const [activeChip, setActiveChip] = useState<'all' | 'boosted' | 'active' | 'pending'>('all')
  const [filterOpen, setFilterOpen] = useState(false)
  const [loading, setLoading] = useState(authed)
  const [error, setError] = useState<string | null>(null)
  const [items, setItems] = useState<BusinessProfile[]>([])
  const [boosted, setBoosted] = useState<BusinessProfile[]>([])
  const [sponsored, setSponsored] = useState<SponsoredCallAdBusiness[]>([])
  const [discoverItems, setDiscoverItems] = useState<BusinessProfile[]>([])
  const [boostedLoading, setBoostedLoading] = useState(true)
  const [sponsoredLoading, setSponsoredLoading] = useState(false)
  const [discoverLoading, setDiscoverLoading] = useState(false)
  const [discoverLoadingMore, setDiscoverLoadingMore] = useState(false)
  const [discoverError, setDiscoverError] = useState<string | null>(null)
  const [discoverPage, setDiscoverPage] = useState(1)
  const [discoverHasNext, setDiscoverHasNext] = useState(false)
  const [q, setQ] = useState(qParam)
  const [createOpen, setCreateOpen] = useState(false)
  const [discoverBookingOpen, setDiscoverBookingOpen] = useState(false)
  const [discoverBookingTarget, setDiscoverBookingTarget] = useState<{ hostRef: string; displayName: string } | null>(null)

  useEffect(() => {
    setQ(qParam)
  }, [qParam])

  const filters = useMemo(() => parsePagesFilters(searchParams, authed), [searchParams, authed])
  const createRequested = searchParams.get('create') === '1'

  const setCreateQuery = (enabled: boolean) => {
    const next = new URLSearchParams(searchParams)
    if (enabled) next.set('create', '1')
    else next.delete('create')
    setSearchParams(next, { replace: true })
  }

  const kickToLoginForCreate = () => {
    setCreateBusinessIntent({
      originPath: `${window.location.pathname}${window.location.search}`,
    })
    navigate(`/login?return_to=${encodeURIComponent('/pages?create=1')}`)
  }

  useEffect(() => {
    if (!createRequested) return
    if (authed) {
      setCreateOpen(true)
      return
    }
    kickToLoginForCreate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createRequested, authed])

  useEffect(() => {
    let cancelled = false
    setBoostedLoading(true)
    boostedBusinesses()
      .then((rows) => {
        if (!cancelled) setBoosted(rows)
      })
      .catch(() => {
        if (!cancelled) setBoosted([])
      })
      .finally(() => {
        if (!cancelled) setBoostedLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!authed) {
      setLoading(false)
      setItems([])
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    myBusinesses()
      .then((rows) => {
        if (!cancelled) setItems(rows)
      })
      .catch((e: any) => {
        if (cancelled) return
        setError(e?.response?.data?.detail || e?.message || 'Failed to load pages')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [authed])

  const discoverCat = useMemo(() => filters.categories.join(','), [filters.categories])

  const loadDiscover = async (page: number, append: boolean) => {
    if (append) setDiscoverLoadingMore(true)
    else setDiscoverLoading(true)
    if (!append) setDiscoverError(null)
    try {
      const data = await discoverBusinesses({
        q: q.trim() || undefined,
        cat: discoverCat || undefined,
        sort: filters.sort,
        open_now: filters.openNow || undefined,
        bookable_today: filters.bookableToday || undefined,
        has_booking: filters.hasBooking || undefined,
        page,
        page_size: 12,
      })
      setDiscoverPage(data.page)
      setDiscoverHasNext(data.has_next)
      setDiscoverItems((prev) => (append ? [...prev, ...data.results] : data.results))
    } catch (e: any) {
      if (!append) setDiscoverItems([])
      setDiscoverError(e?.response?.data?.detail || e?.message || 'Failed to load discover pages')
      if (!append) setDiscoverHasNext(false)
    } finally {
      if (append) setDiscoverLoadingMore(false)
      else setDiscoverLoading(false)
    }
  }

  useEffect(() => {
    void loadDiscover(1, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, discoverCat, filters.sort, filters.openNow, filters.bookableToday, filters.hasBooking])

  useEffect(() => {
    let cancelled = false
    setSponsoredLoading(true)
    sponsoredCallAds({
      q: q.trim() || undefined,
      cat: discoverCat || undefined,
      limit: 3,
    })
      .then((rows) => {
        if (!cancelled) setSponsored(rows)
      })
      .catch(() => {
        if (!cancelled) setSponsored([])
      })
      .finally(() => {
        if (!cancelled) setSponsoredLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [q, discoverCat])

  const categories = useMemo(() => {
    const defaults = ['Food', 'Health', 'Home Services', 'Beauty', 'Professional Services', 'Telecommunications']
    const derived = [...items, ...boosted, ...discoverItems]
      .map((b) => String(b.category || '').trim())
      .filter(Boolean)
      .filter((value, i, arr) => arr.findIndex((v) => normalizeCategory(v) === normalizeCategory(value)) === i)
      .sort((a, b) => a.localeCompare(b))
    return derived.length > 0 ? derived : defaults
  }, [items, boosted, discoverItems])

  const filterBySearch = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (rows: BusinessProfile[]) => rows.filter((b) => {
      if (!needle) return true
      return (
        String(b.name || '').toLowerCase().includes(needle) ||
        String(b.category || '').toLowerCase().includes(needle) ||
        String(b.slug || '').toLowerCase().includes(needle) ||
        String(b.address || '').toLowerCase().includes(needle)
      )
    })
  }, [q])

  const filterByCategory = useMemo(() => {
    return (rows: BusinessProfile[]) => {
      if (filters.categories.length === 0) return rows
      return rows.filter((b) => filters.categories.includes(normalizeCategory(b.category)))
    }
  }, [filters.categories])

  const sortBusinesses = useMemo(() => {
    return (rows: BusinessProfile[]) => {
      const next = rows.slice()
      if (filters.sort === 'alphabetical') {
        next.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
        return next
      }
      if (filters.sort === 'reputation') {
        next.sort((a, b) => Number(b.reputation_score || 0) - Number(a.reputation_score || 0))
        return next
      }
      next.sort((a, b) => {
        const aTs = new Date(a.created_at || 0).getTime()
        const bTs = new Date(b.created_at || 0).getTime()
        return bTs - aTs
      })
      return next
    }
  }, [filters.sort])

  const boostedFiltered = useMemo(() => {
    return sortBusinesses(filterByCategory(filterBySearch(boosted)))
  }, [boosted, filterByCategory, filterBySearch, sortBusinesses])

  const myBusinessesFiltered = useMemo(() => {
    let rows = filterBySearch(items)
    rows = filterByCategory(rows)
    if (!filters.includePending) rows = rows.filter((b) => b.setup_fee_paid)
    if (activeChip === 'active') rows = rows.filter((b) => b.setup_fee_paid)
    if (activeChip === 'pending') rows = rows.filter((b) => !b.setup_fee_paid)
    return sortBusinesses(rows)
  }, [activeChip, filterByCategory, filterBySearch, filters.includePending, items, sortBusinesses])

  const showBoostedSection = activeChip !== 'pending' && !filters.mineOnly
  const showSponsoredSection = activeChip !== 'pending' && activeChip !== 'boosted' && !filters.boostedOnly && !filters.mineOnly
  const showDiscoverSection = activeChip !== 'pending' && activeChip !== 'boosted' && !filters.boostedOnly && !filters.mineOnly
  const showMyBusinessesSection = authed && activeChip !== 'boosted' && !filters.boostedOnly

  function onApplyFilters(next: PagesFilters) {
    const updated = writePagesFilters(searchParams, next, authed)
    setSearchParams(updated, { replace: true })
  }

  function onResetFilters() {
    const updated = writePagesFilters(searchParams, { ...DEFAULT_PAGES_FILTERS }, authed)
    setSearchParams(updated, { replace: true })
  }

  const onClickCreate = () => {
    if (authed) {
      setCreateOpen(true)
      setCreateQuery(true)
      return
    }
    kickToLoginForCreate()
  }

  const openDiscoverBooking = (hostRef: string, displayName: string) => {
    setDiscoverBookingTarget({ hostRef, displayName })
    setDiscoverBookingOpen(true)
  }

  return (
    <div className="min-h-screen">
      <NavBar mobileFixed />

      <main className="mx-auto max-w-6xl px-4 pb-6 pt-[calc(var(--nav-h,56px)+3.25rem)] md:py-6">
        <div className="mb-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-2 overflow-x-auto whitespace-nowrap">
              {[
                { key: 'all', label: 'All' },
                { key: 'boosted', label: 'Boosted' },
                { key: 'active', label: 'Active' },
                { key: 'pending', label: 'Pending' },
              ].map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setActiveChip(chip.key as typeof activeChip)}
                  className={`px-2.5 py-1 text-xs md:text-sm rounded-full border ${
                    activeChip === chip.key ? 'bg-brand border-transparent' : 'bg-white/5 border-white/10'
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                aria-label="Filters"
                onClick={() => setFilterOpen(true)}
                className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/15 bg-white/5 hover:bg-white/10"
                title="Filter Pages"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white/85" aria-hidden="true">
                  <path d="M3 4h18l-7.5 9v5.5l-3 1.5V13L3 4z" />
                </svg>
              </button>
              <button
                type="button"
                onClick={onClickCreate}
                className="inline-flex items-center rounded-full bg-brand px-3 py-1.5 text-xs md:text-sm font-semibold text-white shadow hover:bg-brand-600"
              >
                + Create Page
              </button>
            </div>
          </div>
        </div>

        {showBoostedSection ? (
        <section className="mt-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xl font-extrabold">Boosted</h2>
            <span className="text-xs text-white/60">Paid placement, clearly labeled</span>
          </div>

          {boostedLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-xl bg-white/5 h-56" />
              ))}
            </div>
          ) : null}

          {!boostedLoading && boostedFiltered.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {boostedFiltered.map((b) => (
                <BusinessPageCard
                  key={`boosted-${b.id}`}
                  name={b.name}
                  category={b.category}
                  coverImageUrl={b.cover_image_url || null}
                  portraitImageUrl={b.pixe_portrait_image_url || null}
                  logoImageUrl={b.logo_image_url || null}
                  href={`/b/${b.slug}`}
                  badge="Boosted"
                />
              ))}
            </div>
          ) : null}

          {!boostedLoading && boostedFiltered.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">
              No boosted pages right now.
            </div>
          ) : null}
        </section>
        ) : null}

        {showDiscoverSection ? (
        <>
        {showSponsoredSection && !sponsoredLoading && sponsored.length > 0 ? (
        <section className="mt-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xl font-extrabold">Sponsored (Call Ads)</h2>
            <span className="text-xs text-white/60">Paid placement, clearly labeled</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {sponsored.map((b) => (
              <BusinessPageCard
                key={`sponsored-${b.id}-${b.campaign_id}`}
                name={b.name}
                category={b.category}
                coverImageUrl={b.cover_image_url || null}
                portraitImageUrl={b.pixe_portrait_image_url || null}
                logoImageUrl={b.logo_image_url || null}
                href={`/b/${b.slug}`}
                badge="Sponsored"
              />
            ))}
          </div>
        </section>
        ) : null}

        <section className="mt-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xl font-extrabold">Discover</h2>
            <span className="text-xs text-white/60">
              {filters.sort === 'soonest'
                ? 'Sorted by soonest availability'
                : filters.sort === 'distance'
                  ? 'Sorted by distance'
                  : filters.sort === 'reputation'
                    ? 'Sorted by rating'
                    : 'Newest business pages'}
            </span>
          </div>

          {discoverLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="rounded-xl bg-white/5 h-56" />
              ))}
            </div>
          ) : null}

          {!discoverLoading && discoverError ? <div className="text-sm text-red-300">{discoverError}</div> : null}

          {!discoverLoading && !discoverError && discoverItems.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">
              No pages found. Try a different category or search.
            </div>
          ) : null}

          {!discoverLoading && !discoverError && discoverItems.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {discoverItems.map((b) => (
                  <BusinessPageCard
                    key={`discover-${b.id}`}
                    name={b.name}
                    category={b.category}
                    coverImageUrl={b.cover_image_url || null}
                    portraitImageUrl={b.pixe_portrait_image_url || null}
                    logoImageUrl={b.logo_image_url || null}
                    href={b.booking?.enabled ? `/b/${b.slug}#booking` : `/b/${b.slug}`}
                    badge={b.booking?.bookable_today ? 'Bookable today' : b.open_now ? 'Open now' : 'Active'}
                    meta={
                      b.booking?.next_available_at
                        ? `Next available: ${new Date(b.booking.next_available_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                        : b.booking?.enabled
                          ? 'Booking enabled'
                          : null
                    }
                    actionLabel={
                      b.booking?.enabled && b.booking?.has_services && (b.booking?.bookable_today || Boolean(b.booking?.next_available_at))
                        ? (b.booking?.bookable_today ? 'Book today' : 'Book')
                        : null
                    }
                    onAction={
                      b.booking?.enabled && b.booking?.has_services && (b.booking?.bookable_today || Boolean(b.booking?.next_available_at))
                        ? () => openDiscoverBooking(b.slug, b.name)
                        : null
                    }
                  />
                ))}
              </div>
              {discoverHasNext ? (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={() => void loadDiscover(discoverPage + 1, true)}
                    disabled={discoverLoadingMore}
                    className={`rounded-lg border border-white/15 px-4 py-2 text-sm ${discoverLoadingMore ? 'bg-white/10 text-white/50 cursor-not-allowed' : 'bg-white/5 hover:bg-white/10'}`}
                  >
                    {discoverLoadingMore ? 'Loading...' : 'Load more'}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
        </section>
        </>
        ) : null}

        {showMyBusinessesSection ? (
        <section className="mt-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xl font-extrabold">My Businesses</h2>
            {authed ? <Link to="/settings/pages" className="text-sm text-white/75 hover:text-white">Manage</Link> : null}
          </div>

          {!authed ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/75">
              Sign in to see and manage your pages.
            </div>
          ) : null}

          {authed && loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="rounded-xl bg-white/5 h-56" />
              ))}
            </div>
          ) : null}

          {authed && error ? <div className="text-sm text-red-300">{error}</div> : null}

          {authed && !loading && !error && myBusinessesFiltered.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">
              No pages yet. Create your first business page.
            </div>
          ) : null}

          {authed && !loading && !error && myBusinessesFiltered.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {myBusinessesFiltered.map((b) => (
                <BusinessPageCard
                  key={b.id}
                  name={b.name}
                  category={b.category}
                  coverImageUrl={b.cover_image_url || null}
                  portraitImageUrl={b.pixe_portrait_image_url || null}
                  logoImageUrl={b.logo_image_url || null}
                  href={`/b/${b.slug}`}
                  badge={b.setup_fee_paid ? 'Active' : 'Pending'}
                />
              ))}
            </div>
          ) : null}
        </section>
        ) : null}
      </main>
      <PagesFilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
        authed={authed}
        categories={categories}
        onApply={onApplyFilters}
        onReset={onResetFilters}
      />
      <CreateBusinessModal
        open={createOpen}
        originPath="/pages"
        onClose={() => {
          setCreateOpen(false)
          setCreateQuery(false)
        }}
        onCreated={(created) => {
          clearCreateBusinessIntent()
          setCreateOpen(false)
          setCreateQuery(false)
          navigate(`/settings/pages/${created.slug}?tab=overview`)
        }}
      />
      <DiscoverBookingModal
        open={discoverBookingOpen}
        hostType="business"
        hostRef={discoverBookingTarget?.hostRef || ''}
        displayName={discoverBookingTarget?.displayName || 'Business'}
        onClose={() => setDiscoverBookingOpen(false)}
        onBooked={() => {
          setDiscoverBookingOpen(false)
          void loadDiscover(1, false)
        }}
      />
    </div>
  )
}
